import { useEffect, useState } from "react";
import { Check, ListChecks, Send, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { fullAddress, directionLinks, shareText } from "@/components/CardActions";

const TEMPLATES = {
  en: [
    { key: "will-call", label: "They'll call you", text: "These offices will be calling you to schedule your appointments." },
    { key: "treating", label: "Where you'll treat", text: "Here's where you'll be treating. Tap a link for directions." },
    { key: "clinics", label: "Clinics near you", text: "Here are the medical clinics near you." },
  ],
  es: [
    { key: "will-call", label: "Le llamarán", text: "Estas oficinas le llamarán para programar sus citas." },
    { key: "treating", label: "Dónde se tratará", text: "Aquí es donde recibirá su tratamiento. Toque un enlace para obtener indicaciones." },
    { key: "clinics", label: "Clínicas cerca", text: "Aquí están las clínicas médicas cerca de usted." },
  ],
};

const withGreeting = (name, note, lang) => {
  const n = name.trim();
  const body = note.trim();
  if (!n) return body;
  const hi = lang === "es" ? `¡Hola ${n}!` : `Hi ${n}!`;
  return body ? `${hi} ${body}` : hi;
};

export const buildClientListText = (note, locs) => {
  const items = locs.map((l, i) => {
    const d = directionLinks(l);
    const title = l.company ? `${l.company} – ${l.name}` : l.name;
    const badge = l.note ? ` (${l.note})` : "";
    return `${i + 1}. ${title}${badge}\n${fullAddress(l)}\nGoogle Maps: ${d.google}\nApple Maps: ${d.apple}`;
  });
  const intro = note.trim() ? `${note.trim()}\n\n` : "";
  return `${intro}${items.join("\n\n")}`;
};

const TIP_KEY = "client_list_tip_seen";

const useListTip = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (localStorage.getItem(TIP_KEY)) return;
    let t;
    const arm = () => (t = setTimeout(() => setShow(true), 2500));
    if (sessionStorage.getItem("refer_coach_seen")) arm();
    else window.addEventListener("refer-coach-closed", arm, { once: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("refer-coach-closed", arm);
    };
  }, []);
  const dismiss = () => {
    setShow(false);
    localStorage.setItem(TIP_KEY, "1");
  };
  return [show, dismiss];
};

export const ClientListToggle = ({ active, onToggle }) => {
  const [tip, dismissTip] = useListTip();
  return (
    <div className="relative">
      <button
        data-testid="client-list-toggle"
        onClick={() => {
          dismissTip();
          onToggle();
        }}
        className={`mt-2.5 w-full inline-flex items-center justify-center gap-2 text-xs font-semibold rounded-lg px-3 py-2 border transition-colors ${
          active ? "bg-orange-500 border-orange-400 text-white" : "bg-white/5 border-white/10 text-slate-200 hover:bg-white/10"
        } ${tip ? "ring-2 ring-orange-300 ring-offset-2 ring-offset-[#111D4A]" : ""}`}
      >
        <ListChecks className="w-4 h-4" />
        {active ? "Tap providers to add · Done" : "Build a client list"}
      </button>
      {tip && (
        <div data-testid="client-list-tip" className="absolute top-full left-0 right-0 mt-3 z-30 coach-pop">
          <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-slate-900/95 border-l border-t border-orange-400/40" />
          <div className="relative bg-slate-900/95 backdrop-blur-xl border border-orange-400/40 rounded-xl shadow-2xl p-3 pr-8">
            <button data-testid="client-list-tip-close" onClick={dismissTip} aria-label="Close tip" className="absolute top-2 right-2 text-slate-400 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
            <p className="text-sm font-bold text-white">Share several providers at once</p>
            <p className="text-xs text-slate-300 mt-1 leading-snug">
              Tap here, check the providers your client will see, then send them one text with addresses and directions.
            </p>
            <button
              data-testid="client-list-tip-cta"
              onClick={() => {
                dismissTip();
                onToggle();
              }}
              className="mt-2 text-xs font-semibold text-orange-300 hover:text-orange-200 underline underline-offset-2"
            >
              Try it now →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const PickMark = ({ picked, id }) => (
  <span
    data-testid={`pick-mark-${id}`}
    className={`w-6 h-6 rounded-md border flex items-center justify-center transition-colors ${
      picked ? "bg-orange-500 border-orange-400 text-white" : "border-white/30 text-transparent"
    }`}
  >
    <Check className="w-4 h-4" />
  </span>
);

export const ClientListBar = ({ picked, onClear }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [name, setName] = useState("");
  const [lang, setLang] = useState("en");
  const switchLang = (l) => {
    const i = TEMPLATES[lang].findIndex((t) => t.text === note);
    if (i >= 0) setNote(TEMPLATES[l][i].text);
    setLang(l);
  };
  if (!picked.length) return null;
  const text = buildClientListText(withGreeting(name, note, lang), picked);
  return (
    <>
      <div data-testid="client-list-bar" className="p-3 border-t border-white/10 bg-[#0B132B] flex items-center gap-2">
        <span className="text-sm text-white font-semibold flex-1" data-testid="client-list-count">
          {picked.length} selected
        </span>
        <button data-testid="client-list-clear" onClick={onClear} className="text-xs text-slate-400 hover:text-white px-2 py-2 inline-flex items-center gap-1">
          <X className="w-3.5 h-3.5" /> Clear
        </button>
        <button
          data-testid="client-list-share"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-orange-500 hover:bg-orange-400 rounded-lg px-4 py-2 transition-colors"
        >
          <Send className="w-4 h-4" /> Share
        </button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="client-list-dialog" className="bg-[#111D4A] border-white/15 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Share with your client</DialogTitle>
            <DialogDescription className="text-slate-400">
              {picked.length} provider{picked.length > 1 ? "s" : ""} with addresses and directions.
            </DialogDescription>
          </DialogHeader>
          <input
            data-testid="client-list-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Client first name (optional)"
            className="w-full bg-[#1A275B] border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:ring-2 focus:ring-orange-400 focus:outline-none"
          />
          <div className="flex items-center gap-1 bg-black/20 rounded-full p-1 w-fit" data-testid="client-list-lang">
            {[["en", "English"], ["es", "Español"]].map(([k, l]) => (
              <button
                key={k}
                data-testid={`lang-${k}`}
                onClick={() => switchLang(k)}
                className={`text-[11px] font-semibold rounded-full px-3 py-1 transition-colors ${lang === k ? "bg-white text-[#0B132B]" : "text-slate-300 hover:bg-white/10"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="client-list-templates">
            {TEMPLATES[lang].map((t) => (
              <button
                key={t.key}
                data-testid={`template-${t.key}`}
                onClick={() => setNote(t.text)}
                className={`text-[11px] font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                  note === t.text ? "bg-orange-500 border-orange-400 text-white" : "bg-white/5 border-white/15 text-slate-200 hover:bg-white/10"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <Textarea
            data-testid="client-list-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Pick a message above or type your own (optional)"
            className="bg-[#1A275B] border-white/15 text-white placeholder:text-slate-500"
          />
          <pre data-testid="client-list-preview" className="max-h-48 overflow-y-auto dfw-scroll whitespace-pre-wrap [overflow-wrap:anywhere] text-[11px] text-slate-300 bg-black/20 rounded-lg p-3 font-sans">
            {text}
          </pre>
          <button
            data-testid="client-list-send"
            onClick={() => shareText("Your providers", text)}
            className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-orange-500 hover:bg-orange-400 rounded-lg px-4 py-2.5 transition-colors"
          >
            <Send className="w-4 h-4" /> Send to client
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};
